<template>
  <div class="forgot-password">
    <div
      class="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8"
    >
      <div class="max-w-md w-full space-y-8 bg-card rounded-xl border border-border shadow-sm p-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-foreground">
            {{ $t('auth.resetPasswordTitle') }}
          </h2>
          <p class="mt-2 text-center text-sm text-muted-foreground">
            {{ $t('auth.or') }}
            <router-link
              :to="localePath('Login')"
              class="font-medium text-primary hover:text-primary/80"
            >
              {{ $t('auth.goBackToSignIn') }}
            </router-link>
          </p>
        </div>

        <AlertBanner v-if="success" variant="success" class="mt-8">
          <p class="text-sm">{{ successMessage }}</p>
        </AlertBanner>

        <form v-else class="mt-8 space-y-6" novalidate @submit.prevent="handleSubmit">
          <AlertBanner v-if="error" variant="error">
            <p class="text-sm">{{ error }}</p>
          </AlertBanner>

          <div class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-foreground mb-1">{{
                $t('auth.emailLabel')
              }}</label>
              <input
                id="email"
                v-model="email"
                type="email"
                :class="[
                  'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                  fieldErrors.email
                    ? 'border-destructive focus:ring-destructive focus:border-destructive'
                    : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                ]"
                :placeholder="$t('auth.emailPlaceholder')"
              />
              <p v-if="fieldErrors.email" class="mt-1 text-sm text-destructive">
                {{ fieldErrors.email }}
              </p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="loading"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LoadingSpinner :loading="loading" />
              {{ $t('auth.sendResetInstructions') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocalePath } from '../../composables/useLocalePath'
import apiClient from '../../api/client'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import AlertBanner from '../../components/AlertBanner.vue'

const { t, locale } = useI18n()
const { localePath } = useLocalePath()

const email = ref('')
const loading = ref(false)
const error = ref(null)
const success = ref(false)
const successMessage = ref('')
const fieldErrors = reactive({})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validate = () => {
  delete fieldErrors.email

  if (!email.value.trim()) {
    fieldErrors.email = t('auth.emailRequired')
  } else if (!emailRegex.test(email.value.trim())) {
    fieldErrors.email = t('auth.emailInvalid')
  }

  return Object.keys(fieldErrors).length === 0
}

watch(email, () => delete fieldErrors.email)

const handleSubmit = async () => {
  error.value = null
  if (!validate()) return

  loading.value = true

  try {
    await apiClient.post('/auth/password', { email: email.value, locale: locale.value })
    successMessage.value = t('auth.resetInstructionsSent')
    success.value = true
  } catch (error_) {
    error.value = error_.response?.data?.error || t('common.somethingWentWrong')
  } finally {
    loading.value = false
  }
}
</script>
